import { ElementType, Fragment } from 'react';

const URL_PATTERN = /\b((?:https?:\/\/|www\.)[^\s<]+)/gi;
const TRAILING_PUNCTUATION_PATTERN = /[.,!?;:)]+$/;

const getLinkParts = (value: string) => {
    const trailingPunctuation = value.match(TRAILING_PUNCTUATION_PATTERN)?.[0];

    if (!trailingPunctuation) {
        return { linkText: value, trailingText: '' };
    }

    return {
        linkText: value.slice(0, -trailingPunctuation.length),
        trailingText: trailingPunctuation,
    };
};

const getHref = (value: string) =>
    value.toLowerCase().startsWith('www.') ? `https://${value}` : value;

type LinkifiedTextProps = {
    text: string;
    className?: string;
    as?: ElementType;
};

const wrappingClassName =
    'min-w-0 max-w-full break-words [overflow-wrap:anywhere] [word-break:break-word]';

export const LinkifiedText = ({
    text,
    className,
    as: Component = 'span',
}: LinkifiedTextProps) => {
    const nodes = [];
    let lastIndex = 0;

    for (const match of text.matchAll(URL_PATTERN)) {
        const rawUrl = match[0];
        const index = match.index ?? 0;

        if (index > lastIndex) {
            nodes.push(text.slice(lastIndex, index));
        }

        const { linkText, trailingText } = getLinkParts(rawUrl);

        nodes.push(
            <Fragment key={`${index}-${rawUrl}`}>
                <a
                    href={getHref(linkText)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-woodyBrown underline hover:opacity-80 ${wrappingClassName}`}
                >
                    {linkText}
                </a>
                {trailingText}
            </Fragment>,
        );

        lastIndex = index + rawUrl.length;
    }

    if (lastIndex < text.length) {
        nodes.push(text.slice(lastIndex));
    }

    return (
        <Component
            className={
                className
                    ? `${wrappingClassName} ${className}`
                    : wrappingClassName
            }
        >
            {nodes}
        </Component>
    );
};
